/**
 * ProfileManager.ts - Core Profylr Profile Management
 * 
 * CHANGE HISTORY:
 * - 251102_1441 danielbeer: Initial ProfileManager implementation
 * 
 * ARCHITECTURE:
 * - Integrates with Actr for all CRUD operations (logging, permissions)
 * - Uses Aggregatr for profile storage/persistence
 * - Provides profile resolution with inheritance
 * - Manages active profile context
 */

import type {
  Profile,
  ResolvedProfile,
  ProfileQueryOptions,
  ProfileUpdateOptions,
  ProfileExport,
  ProfileScope,
} from './types';

/**
 * ProfileManager - Core profile management
 * 
 * INTEGRATION PATTERN:
 * - All CRUD through Actr (automatic logging, permissions)
 * - Storage via Aggregatr (persistence layer)
 * - Used by all 5 orchestrators for context
 */
export class ProfileManager {
  private actr: any;
  private aggregatr: any;
  private profiles: Map<string, Profile> = new Map();
  private activeProfileId: string | null = null;
  private profileStack: string[] = []; // For scope resolution
  
  constructor(options: { actr?: any; aggregatr?: any } = {}) {
    this.actr = options.actr;
    this.aggregatr = options.aggregatr;
    
    // Register actions with Actr
    if (this.actr) {
      this._registerActions();
    }
  }
  
  /**
   * Register Profylr actions with Actr
   * All CRUD operations go through Actr for logging and permissions
   */
  private _registerActions(): void {
    this.actr.register('profylr:create', {
      handler: this._createProfileAction.bind(this),
      description: 'Create profile',
      permissions: ['profile:create']
    });
    this.actr.register('profylr:read', {
      handler: this._readProfileAction.bind(this),
      description: 'Read profile',
      permissions: ['profile:read']
    });
    this.actr.register('profylr:update', {
      handler: this._updateProfileAction.bind(this),
      description: 'Update profile',
      permissions: ['profile:update']
    });
    this.actr.register('profylr:delete', {
      handler: this._deleteProfileAction.bind(this),
      description: 'Delete profile',
      permissions: ['profile:delete']
    });
    this.actr.register('profylr:list', {
      handler: this._listProfilesAction.bind(this),
      description: 'List profiles',
      permissions: ['profile:read']
    });
    this.actr.register('profylr:activate', {
      handler: this._activateProfileAction.bind(this),
      description: 'Activate profile',
      permissions: ['profile:activate']
    });
    this.actr.register('profylr:resolve', {
      handler: this._resolveProfileAction.bind(this),
      description: 'Resolve profile with inheritance',
      permissions: ['profile:read']
    });
  }
  
  /**
   * Create a new profile
   * Goes through Actr for logging and permissions
   */
  async create(profileData: Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>): Promise<Profile> {
    if (this.actr) {
      return await this.actr.execute('profylr:create', { profileData });
    }
    return this._createProfileAction({ profileData });
  }
  
  /**
   * Internal: Create profile action handler
   */
  private async _createProfileAction({ profileData }: { profileData: any }): Promise<Profile> {
    const profile: Profile = {
      ...profileData,
      id: profileData.id || this._generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    // Validate profile
    this._validateProfile(profile);
    
    // Store in memory
    this.profiles.set(profile.id, profile);
    
    // Persist via Aggregatr
    if (this.aggregatr) {
      await this.aggregatr.create('profiles', profile);
    }
    
    return profile;
  }
  
  /**
   * Read a profile by ID
   */
  async read(profileId: string): Promise<Profile | null> {
    if (this.actr) {
      return await this.actr.execute('profylr:read', { profileId });
    }
    return this._readProfileAction({ profileId });
  }
  
  /**
   * Internal: Read profile action handler
   */
  private async _readProfileAction({ profileId }: { profileId: string }): Promise<Profile | null> {
    // Check memory first
    if (this.profiles.has(profileId)) {
      return this.profiles.get(profileId)!;
    }
    
    // Load from Aggregatr
    if (this.aggregatr) {
      const profile = await this.aggregatr.read('profiles', profileId);
      if (profile) {
        this.profiles.set(profileId, profile);
        return profile;
      }
    }
    
    return null;
  }
  
  /**
   * Update a profile
   */
  async update(
    profileId: string,
    updates: Partial<Profile>,
    options: ProfileUpdateOptions = {}
  ): Promise<Profile> {
    if (this.actr) {
      return await this.actr.execute('profylr:update', { profileId, updates, options });
    }
    return this._updateProfileAction({ profileId, updates, options });
  }
  
  /**
   * Internal: Update profile action handler
   */
  private async _updateProfileAction({
    profileId,
    updates,
    options = {},
  }: {
    profileId: string;
    updates: Partial<Profile>;
    options?: ProfileUpdateOptions;
  }): Promise<Profile> {
    const existing = await this.read(profileId);
    if (!existing) {
      throw new Error(`Profile not found: ${profileId}`);
    }
    
    const updated: Profile = options.merge
      ? this._mergeProfiles(existing, updates)
      : { ...existing, ...updates, updatedAt: new Date().toISOString() };
    
    if (options.validate) {
      this._validateProfile(updated);
    }
    
    this.profiles.set(profileId, updated);
    
    if (this.aggregatr) {
      await this.aggregatr.update('profiles', profileId, updated);
    }
    
    return updated;
  }
  
  /**
   * Delete a profile
   */
  async delete(profileId: string): Promise<boolean> {
    if (this.actr) {
      return await this.actr.execute('profylr:delete', { profileId });
    }
    return this._deleteProfileAction({ profileId });
  }
  
  /**
   * Internal: Delete profile action handler
   */
  private async _deleteProfileAction({ profileId }: { profileId: string }): Promise<boolean> {
    this.profiles.delete(profileId);
    
    if (this.aggregatr) {
      await this.aggregatr.delete('profiles', profileId);
    }
    
    // Clear active if this was active
    if (this.activeProfileId === profileId) {
      this.activeProfileId = null;
    }
    
    return true;
  }
  
  /**
   * List profiles with optional filtering
   */
  async list(options: ProfileQueryOptions = {}): Promise<Profile[]> {
    if (this.actr) {
      return await this.actr.execute('profylr:list', { options });
    }
    return this._listProfilesAction({ options });
  }
  
  /**
   * Internal: List profiles action handler
   */
  private async _listProfilesAction({ options = {} }: { options?: ProfileQueryOptions }): Promise<Profile[]> {
    // Load all from Aggregatr if not in memory
    if (this.aggregatr && this.profiles.size === 0) {
      const profiles = await this.aggregatr.list('profiles');
      profiles.forEach((p: Profile) => this.profiles.set(p.id, p));
    }
    
    let results = Array.from(this.profiles.values());
    
    // Apply filters
    if (options.scope) {
      results = results.filter(p => p.scope === options.scope);
    }
    if (options.tags && options.tags.length > 0) {
      results = results.filter(p => 
        p.tags && options.tags!.some(tag => p.tags!.includes(tag))
      );
    }
    if (options.active !== undefined) {
      results = results.filter(p => p.isActive === options.active);
    }
    
    return results;
  }
  
  /**
   * Activate a profile (make it the active context)
   */
  async activate(profileId: string): Promise<void> {
    if (this.actr) {
      return await this.actr.execute('profylr:activate', { profileId });
    }
    return this._activateProfileAction({ profileId });
  }
  
  /**
   * Internal: Activate profile action handler
   */
  private async _activateProfileAction({ profileId }: { profileId: string }): Promise<void> {
    const profile = await this.read(profileId);
    if (!profile) {
      throw new Error(`Profile not found: ${profileId}`);
    }
    
    this.activeProfileId = profileId;
    
    // Update profile stack for scope resolution
    this._updateProfileStack(profile);
  }
  
  /**
   * Get the currently active profile (resolved with inheritance)
   */
  async getActiveProfile(): Promise<ResolvedProfile | null> {
    if (!this.activeProfileId) {
      return null;
    }
    
    if (this.actr) {
      return await this.actr.execute('profylr:resolve', { profileId: this.activeProfileId });
    }
    return this._resolveProfileAction({ profileId: this.activeProfileId });
  }
  
  /**
   * Resolve a profile with inheritance
   * Merges parent profiles to create final resolved profile
   */
  async resolve(profileId: string): Promise<ResolvedProfile> {
    if (this.actr) {
      return await this.actr.execute('profylr:resolve', { profileId });
    }
    return this._resolveProfileAction({ profileId });
  }
  
  /**
   * Internal: Resolve profile action handler
   */
  private async _resolveProfileAction({ profileId }: { profileId: string }): Promise<ResolvedProfile> {
    const profile = await this.read(profileId);
    if (!profile) {
      throw new Error(`Profile not found: ${profileId}`);
    }
    
    const resolvedFrom: string[] = [profileId];
    let resolved: any = { ...profile };
    
    // Resolve inheritance chain
    if (profile.inherits) {
      const parent = await this.resolve(profile.inherits);
      resolved = this._mergeProfiles(parent, profile);
      resolvedFrom.unshift(...parent.resolvedFrom);
    }
    
    return {
      ...resolved,
      resolvedFrom,
      overrides: this._calculateOverrides(profile, resolved),
    };
  }
  
  /**
   * Export profiles for backup/sharing
   */
  async export(profileIds?: string[]): Promise<ProfileExport> {
    const profiles = profileIds
      ? await Promise.all(profileIds.map(id => this.read(id))).then(ps => ps.filter(Boolean) as Profile[])
      : await this.list();
    
    return {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      profiles,
    };
  }
  
  /**
   * Import profiles from export
   */
  async import(exportData: ProfileExport): Promise<Profile[]> {
    const imported: Profile[] = [];
    
    for (const profileData of exportData.profiles) {
      const profile = await this.create(profileData);
      imported.push(profile);
    }
    
    return imported;
  }
  
  /**
   * Helper: Merge two profiles (child overrides parent)
   */
  private _mergeProfiles(parent: Partial<Profile>, child: Partial<Profile>): Profile {
    return {
      ...parent,
      ...child,
      dataBoundaries: {
        ...parent.dataBoundaries,
        ...child.dataBoundaries,
        datasets: [
          ...(parent.dataBoundaries?.datasets || []),
          ...(child.dataBoundaries?.datasets || []),
        ],
        excludeDatasets: [
          ...(parent.dataBoundaries?.excludeDatasets || []),
          ...(child.dataBoundaries?.excludeDatasets || []),
        ],
      },
      perspective: {
        ...parent.perspective,
        ...child.perspective,
      },
      context: {
        ...parent.context,
        ...child.context,
      },
      access: {
        ...parent.access,
        ...child.access,
      },
      behavior: {
        ...parent.behavior,
        ...child.behavior,
      },
    } as Profile;
  }
  
  /**
   * Helper: Calculate what was overridden from parent
   */
  private _calculateOverrides(original: Profile, resolved: Profile): Record<string, any> {
    const overrides: Record<string, any> = {};
    
    // Compare each section
    const sections = ['dataBoundaries', 'perspective', 'context', 'access', 'behavior'];
    for (const section of sections) {
      const originalSection = (original as any)[section];
      const resolvedSection = (resolved as any)[section];
      
      if (originalSection && resolvedSection) {
        for (const key in originalSection) {
          if (originalSection[key] !== resolvedSection[key]) {
            if (!overrides[section]) overrides[section] = {};
            overrides[section][key] = originalSection[key];
          }
        }
      }
    }
    
    return overrides;
  }
  
  /**
   * Helper: Update profile stack for scope resolution
   */
  private _updateProfileStack(profile: Profile): void {
    // Clear stack
    this.profileStack = [];
    
    // Build stack based on scope hierarchy
    // Global → App → View → User → Session
    const scopeOrder: ProfileScope[] = ['global', 'app', 'view', 'user', 'session'];
    const profileScope = profile.scope;
    const scopeIndex = scopeOrder.indexOf(profileScope);
    
    // Add all profiles up to current scope
    for (let i = 0; i <= scopeIndex; i++) {
      const scopeProfiles = Array.from(this.profiles.values())
        .filter(p => p.scope === scopeOrder[i] && p.isActive);
      this.profileStack.push(...scopeProfiles.map(p => p.id));
    }
  }
  
  /**
   * Helper: Validate profile structure
   */
  private _validateProfile(profile: Profile): void {
    if (!profile.id) throw new Error('Profile must have an id');
    if (!profile.name) throw new Error('Profile must have a name');
    if (!profile.scope) throw new Error('Profile must have a scope');
    
    // Validate scope
    const validScopes: ProfileScope[] = ['global', 'app', 'view', 'user', 'session'];
    if (!validScopes.includes(profile.scope)) {
      throw new Error(`Invalid scope: ${profile.scope}`);
    }
  }
  
  /**
   * Helper: Generate unique profile ID
   */
  private _generateId(): string {
    return `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Singleton instance
 */
let instance: ProfileManager | null = null;

/**
 * Get singleton ProfileManager instance
 */
export function getProfylr(options?: { actr?: any; aggregatr?: any }): ProfileManager {
  if (!instance) {
    instance = new ProfileManager(options);
  }
  return instance;
}

export default ProfileManager;
